import { createEaterAuthClient, } from "./EaterAuthClient";
/**
 * Whether an Eater is signed in on the Site, and the login that signs one in.
 * The cookie is HttpOnly, so only Nova can tell; asking it also extends the
 * session.
 */
export class EaterSession {
    /** Asks Nova for the session right away. */
    static create(locale) {
        const session = new EaterSession(createEaterAuthClient(locale));
        session.firstCheck = session.check();
        return session;
    }
    constructor(client) {
        this.client = client;
        this._state = { status: "loading" };
        this.listeners = new Set();
        this.firstCheck = Promise.resolve();
    }
    // A failed check counts as signed out: the login then shows the error.
    // A sign-in or out made meanwhile is newer than the check's answer.
    check() {
        const settle = (eater) => {
            if (this._state.status !== "loading")
                return;
            this.set(eater !== null
                ? { status: "signed-in", eater }
                : { status: "signed-out" });
        };
        return this.client.session().then(settle, () => settle(null));
    }
    get state() {
        return this._state;
    }
    ready() {
        return this.firstCheck.then(() => this._state);
    }
    requestCode(email) {
        return this.client.sendCode(email);
    }
    verifyCode(email, code) {
        return this.client.signIn(email, code).then((result) => {
            if (result.type !== "signed-in")
                return result;
            this.set({ status: "signed-in", eater: result.eater });
            return { type: "signed-in" };
        });
    }
    /** Rejects when Nova can't end the session, which leaves the Eater signed in. */
    signOut() {
        return this.client.signOut().then(() => {
            this.set({ status: "signed-out" });
        });
    }
    /** Called when the status or the signed-in Eater changes. */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    set(next) {
        const changed = userKey(next) !== userKey(this._state);
        this._state = next;
        if (changed)
            this.listeners.forEach((listener) => listener(next));
    }
}
function userKey(state) {
    return state.status === "signed-in"
        ? `user:${state.eater.user.id}`
        : state.status;
}
// Module state, not a container singleton: the every-page bundle stays free of tsyringe and the client lib.
let session = null;
/** The page's session, made on the first call. Call only on a Site with Eater login. */
export function eaterSession(locale) {
    if (session === null)
        session = EaterSession.create(locale);
    return session;
}
